import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, CreditCard, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function PayInvoice() {
  const { id } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState('');
  const [mode, setMode] = useState('full');
  const [customAmount, setCustomAmount] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/pay/invoice/${id}/info`)
      .then((r) => setInfo(r.data))
      .catch(() => setError('Invoice not found'));
  }, [id]);

  const amount = mode === 'full' ? (info?.balance_due || 0) : parseFloat(customAmount) || 0;

  const payNow = async () => {
    setBusy(true);
    setError('');
    try {
      const res = await axios.post(`${BACKEND_URL}/api/pay/invoice/${id}/checkout`, { amount });
      window.location.href = res.data.checkout_url;
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not start the payment. Please try again.');
      setBusy(false);
    }
  };

  if (error && !info) {
    return <div className="min-h-screen flex items-center justify-center text-gray-600">{error}</div>;
  }
  if (!info) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-green-600" size={40} /></div>;
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-10">
      <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full" data-testid="pay-invoice-card">
        <h1 className="text-xl font-bold text-gray-900 mb-1">Pay Invoice {info.invoice_number}</h1>
        <p className="text-gray-500 text-sm mb-6">{info.company_name} · NJK Logistics Pte Ltd</p>

        {info.paid || info.balance_due <= 0 ? (
          <div className="text-center py-6">
            <CheckCircle className="mx-auto text-green-600 mb-3" size={48} />
            <p className="font-semibold text-gray-900">This invoice is already fully paid.</p>
          </div>
        ) : (
          <>
            <div className="bg-gray-50 rounded-lg p-4 mb-6 text-sm space-y-1">
              <div className="flex justify-between"><span className="text-gray-500">Invoice total</span><span className="font-medium">${info.total_amount.toFixed(2)}</span></div>
              {info.amount_paid > 0 && (
                <div className="flex justify-between"><span className="text-gray-500">Already paid</span><span className="font-medium text-green-700">-${info.amount_paid.toFixed(2)}</span></div>
              )}
              <div className="flex justify-between border-t pt-1 mt-1"><span className="font-semibold">Balance due</span><span className="font-bold text-lg" data-testid="pay-balance">${info.balance_due.toFixed(2)}</span></div>
            </div>

            <div className="space-y-3 mb-6">
              <label className={`flex items-center gap-3 border rounded-lg p-3 cursor-pointer ${mode === 'full' ? 'border-green-600 bg-green-50' : 'border-gray-200'}`}>
                <input type="radio" checked={mode === 'full'} onChange={() => setMode('full')} data-testid="pay-full-radio" />
                <div>
                  <p className="font-medium">Pay full balance</p>
                  <p className="text-sm text-gray-500">${info.balance_due.toFixed(2)} — invoice marked paid instantly</p>
                </div>
              </label>
              <label className={`flex items-start gap-3 border rounded-lg p-3 cursor-pointer ${mode === 'partial' ? 'border-green-600 bg-green-50' : 'border-gray-200'}`}>
                <input type="radio" className="mt-1" checked={mode === 'partial'} onChange={() => setMode('partial')} data-testid="pay-partial-radio" />
                <div className="flex-1">
                  <p className="font-medium">Pay a deposit / partial amount</p>
                  {mode === 'partial' && (
                    <div className="mt-2 flex items-center gap-2">
                      <span className="text-gray-500">$</span>
                      <Input type="number" min="1" max={info.balance_due} step="0.01"
                             value={customAmount}
                             onChange={(e) => setCustomAmount(e.target.value)}
                             placeholder={`1.00 – ${info.balance_due.toFixed(2)}`}
                             data-testid="pay-custom-amount" />
                    </div>
                  )}
                </div>
              </label>
            </div>

            {error && <p className="text-red-600 text-sm mb-3" data-testid="pay-error">{error}</p>}

            <Button
              className="w-full bg-green-600 hover:bg-green-700 text-white h-12 text-base"
              onClick={payNow}
              disabled={busy || amount < 1 || amount > info.balance_due + 0.005}
              data-testid="pay-now-btn"
            >
              {busy ? <Loader2 className="animate-spin mr-2" size={18} /> : <CreditCard className="mr-2" size={18} />}
              {busy ? 'Redirecting…' : `Pay $${amount > 0 ? amount.toFixed(2) : '0.00'} Securely`}
            </Button>
            <p className="text-xs text-gray-400 text-center mt-3">Secure card payment powered by Stripe. A receipt is emailed to you automatically.</p>
          </>
        )}
      </div>
    </div>
  );
}
